import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import HeaderFooterLayoutOnboarding from 'layouts/HeaderFooterLayoutOnboarding';
import history from 'store/history';

import LocationSelectionStep from './OnboardingSteps/LocationSelectionStep';
import FitnessGoalStep from './OnboardingSteps/FitnessGoalStep';
import WorkoutSelectionStep from './OnboardingSteps/WorkoutSelectionStep';
import MotivationStep from './OnboardingSteps/MotivationStep';
import FitnessLevelStep from './OnboardingSteps/FitnessLevelStep';
import Spinner from 'components/shared/Spinner';

// Main component that handles step rendering and all onboarding logic
const OnboardingProcess = ({
  loadOnboardingSteps,
  stepsData,
  isLoadingSteps,
  submitOnboardingSteps,
}) => {
  // Internal state management
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState({});

  // Helper function to sort steps by displayOrder (reusable)
  const getSortedSteps = () => {
    if (!stepsData?.steps) return [];

    return [...stepsData.steps].sort((a, b) => {
      // Handle steps without explicit displayOrder
      const orderA = a.displayOrder || a.options?.[0]?.displayOrder || 1;
      const orderB = b.displayOrder || b.options?.[0]?.displayOrder || 1;
      return orderA - orderB;
    });
  };

  // Get step data based on displayOrder (API might not be in sequential order)
  const getCurrentStepData = () => {
    const sortedSteps = getSortedSteps();
    // Get current step data (currentStep is 1-indexed)
    return sortedSteps[currentStep - 1] || null;
  };

  // Load onboarding data on component mount
  useEffect(() => {
    loadOnboardingSteps();
  }, [loadOnboardingSteps]);

  // Initialize onboarding data and current step when API data loads
  useEffect(() => {
    if (stepsData?.steps) {
      const sortedSteps = getSortedSteps();

      // SAFETY CHECK: If user has already completed onboarding, redirect to home
      // This handles the case where user completed onboarding but somehow got redirected to /onboarding
      const finalStep = sortedSteps.find(step => step.isFinal === true);
      const finalStepAnswered = finalStep && finalStep.hasAnswered === true;

      if (finalStepAnswered) {
        // User has already completed the entire onboarding process
        // Mark as completed and redirect to home
        localStorage.setItem('onboardingCompleted', 'true');
        localStorage.removeItem('onboardingInProgress');

        // Redirect to home immediately
        history.push('/');
        return; // Don't continue with normal initialization
      }

      // Initialize onboarding data with existing user responses
      const initialData = {};
      let firstUnansweredStep = null;

      sortedSteps.forEach((step, index) => {
        if (step.hasAnswered && step.userResponse && step.hubspotProperty) {
          // Store the user's previous response in the correct format
          let responseValue = step.userResponse;

          // Handle array responses (extract first value for single selections)
          if (Array.isArray(responseValue) && responseValue.length > 0) {
            responseValue = responseValue[0];
          }

          // Check if the response is the hubspotSkipValue (meaning user skipped)
          const isSkippedValue =
            step.hubspotSkipValue && responseValue === step.hubspotSkipValue;

          // Only store the value if it's not the skip value
          if (!isSkippedValue) {
            initialData[step.hubspotProperty] = responseValue;
          }
          // If it's a skipped value, don't pre-fill anything (leave empty)
        } else if (firstUnansweredStep === null) {
          // Found first unanswered step
          firstUnansweredStep = index + 1;
        }
      });

      // If all steps are answered, go to the last step
      if (firstUnansweredStep === null) {
        firstUnansweredStep = sortedSteps.length;
      }

      // Update state with restored data
      setOnboardingData(initialData);

      // Set current step to first unanswered step (but don't go past available steps)
      if (firstUnansweredStep <= sortedSteps.length) {
        setCurrentStep(firstUnansweredStep);
      }
    }
  }, [stepsData]);

  // Handle completion of a step
  const handleStepComplete = (step, stepData) => {
    // Get current step data for proper state management
    const currentStepData = getCurrentStepData();

    // For location selection, map selectedLocation to hubspotProperty
    let formattedStepData = { ...stepData };
    if (currentStepData?.hubspotProperty && stepData.selectedLocation) {
      formattedStepData[currentStepData.hubspotProperty] = stepData.selectedLocation;
    }

    // Save the step data with proper hubspot property mapping
    setOnboardingData(prevData => ({
      ...prevData,
      ...formattedStepData,
    }));

    if (currentStepData) {
      // Build the API payload based on step type and data
      const payload = buildResponsePayload(currentStepData, stepData, step);

      // Check if this is the final step for immediate redirection
      const isFinalStep =
        currentStepData.isFinal !== undefined
          ? currentStepData.isFinal
          : step === totalSteps;

      if (isFinalStep) {
        // Handle onboarding completion immediately (don't wait for API)
        localStorage.setItem('onboardingCompleted', 'true');
        localStorage.removeItem('onboardingInProgress');

        // Handle routing immediately - only check originalCallback
        // (callback was already processed by login saga)
        const originalCallback = sessionStorage.getItem('originalCallback');

        if (
          originalCallback &&
          originalCallback !== '/' &&
          originalCallback !== '/onboarding'
        ) {
          // Redirect to the original callback URL (pre-login destination)
          history.push(originalCallback);
          sessionStorage.removeItem('originalCallback');
        } else {
          // Default redirect to home page
          history.push('/');
        }

        // Still submit to API in background (fire and forget)
        submitOnboardingSteps(payload);
        return; // Don't move to next step since we're redirecting
      }

      // Submit to API for non-final steps
      submitOnboardingSteps(payload);
    }

    // Move to the next step (only for non-final steps)
    setCurrentStep(step + 1);
  };

  // Build API response payload based on step data and user selections
  const buildResponsePayload = (stepData, userSelections, currentStep) => {
    const responses = [];

    // Handle different step types dynamically
    switch (stepData.type) {
      case 'SingleSelect':
        if (stepData.hubspotProperty) {
          // Look for the value in multiple possible locations:
          // 1. Direct hubspotProperty key (for FitnessGoalStep)
          // 2. selectedLocation key (for LocationSelectionStep)
          // 3. Any other key that matches the pattern
          let selectedValue =
            userSelections[stepData.hubspotProperty] ||
            userSelections.selectedLocation ||
            '';

          // If still empty, check for any value in userSelections object
          if (!selectedValue) {
            const values = Object.values(userSelections);
            selectedValue = values.find(val => val && val !== '') || '';
          }

          responses.push({
            hubspotProperty: stepData.hubspotProperty,
            values: [selectedValue],
          });
        }
        break;

      case 'MultiSelect':
        if (stepData.hubspotProperty) {
          // Look for the values using the hubspotProperty key
          let selectedValues = userSelections[stepData.hubspotProperty];

          // If not found, look for any array values in userSelections
          if (!selectedValues) {
            const values = Object.values(userSelections);
            selectedValues = values.find(val => Array.isArray(val)) || [];
          }

          // Ensure it's an array and handle skip case
          if (!Array.isArray(selectedValues)) {
            selectedValues = selectedValues ? [selectedValues] : [];
          }

          // Handle skip case - if empty array, use hubspotSkipValue from API
          if (selectedValues.length === 0 && stepData.hubspotSkipValue) {
            selectedValues = [stepData.hubspotSkipValue];
          }

          responses.push({
            hubspotProperty: stepData.hubspotProperty,
            values: selectedValues,
          });
        }
        break;

      case 'Slider':
        // Handle multiple sliders dynamically
        if (stepData.sliders && Array.isArray(stepData.sliders)) {
          stepData.sliders.forEach(slider => {
            if (slider.hubspotProperty) {
              const sliderValue = userSelections[slider.hubspotProperty] || '';

              responses.push({
                hubspotProperty: slider.hubspotProperty,
                values: [sliderValue],
              });
            }
          });
        } else {
          // Fallback: if no sliders array, look for slider-like values in userSelections
          Object.keys(userSelections).forEach(key => {
            const value = userSelections[key];
            if ((value && typeof value === 'string') || typeof value === 'number') {
              responses.push({
                hubspotProperty: key,
                values: [String(value)],
              });
            }
          });
        }
        break;

      default:
        // Generic fallback: try to extract any data from userSelections
        if (stepData.hubspotProperty) {
          const value = userSelections[stepData.hubspotProperty] || '';
          responses.push({
            hubspotProperty: stepData.hubspotProperty,
            values: Array.isArray(value) ? value : [value],
          });
        }
    }

    // Determine if onboarding is complete based on backend's isFinal flag
    // If isFinal is explicitly provided by backend, use that
    // Otherwise, fallback to step count logic for backward compatibility
    const isComplete =
      stepData.isFinal !== undefined ? stepData.isFinal : currentStep === totalSteps;

    return {
      responses,
      completed: isComplete,
    };
  };

  // Handle going back to the previous step
  const handleStepBack = currentStep => {
    setCurrentStep(currentStep - 1);
  };

  // Get total steps from API data
  const totalSteps = stepsData?.totalSteps;

  // Get current step data from API
  const currentStepData = stepsData?.steps?.[currentStep - 1];

  // Determine navigation capabilities based on API data
  const canGoBack = currentStep > 1;
  const canSkip = currentStepData?.skippable;
  const isLastStep = currentStep === totalSteps;

  // Render the appropriate step based on currentStep
  if (isLoadingSteps || !stepsData?.steps) {
    return <Spinner />;
  }

  // Map step types and display formats to components
  const getStepComponent = stepData => {
    if (!stepData) return null;

    const { type, options } = stepData;

    // Determine display format from first option (if exists)
    const displayFormat = options?.[0]?.displayFormat;

    // Map based on type and displayFormat
    switch (type) {
      case 'SingleSelect':
        if (displayFormat === 'FavoriteCard') {
          // Location selection with favorite cards
          return LocationSelectionStep;
        } else if (displayFormat === 'PlainText') {
          // Fitness goal selection with plain text
          return FitnessGoalStep;
        }
        return null; // Unknown displayFormat for SingleSelect

      case 'MultiSelect':
        if (displayFormat === 'LogoText') {
          // Workout selection with logo and text
          return WorkoutSelectionStep;
        } else if (displayFormat === 'PlainText') {
          // Motivation selection with plain text
          return MotivationStep;
        }
        return null; // Unknown displayFormat for MultiSelect

      case 'Slider':
        // Fitness level and check-in goals
        return FitnessLevelStep;

      default:
        return null;
    }
  };

  // Get component props based on step data
  const getComponentProps = (stepData, StepComponent) => {
    const baseProps = {
      stepData,
      onBack: canGoBack ? () => handleStepBack(currentStep) : undefined,
    };

    // Component-specific props based on the step type and component
    if (StepComponent === LocationSelectionStep) {
      // Get previously selected location from local state or API response
      const hubspotProperty = stepData.hubspotProperty;
      const previousSelection = onboardingData[hubspotProperty] || '';

      return {
        ...baseProps,
        onNext: selectedLocation => handleStepComplete(currentStep, { selectedLocation }),
        previousSelection, // Pass previous selection to component
        canSkip,
      };
    }

    if (StepComponent === FitnessGoalStep) {
      const hubspotProperty = stepData.hubspotProperty;
      const selectedValue = onboardingData[hubspotProperty] || '';

      return {
        ...baseProps,
        selectedGoal: selectedValue,
        onNext: goal => handleStepComplete(currentStep, { [hubspotProperty]: goal }),
        canSkip,
      };
    }

    if (StepComponent === WorkoutSelectionStep) {
      const hubspotProperty = stepData.hubspotProperty;
      const selectedValues = onboardingData[hubspotProperty] || [];

      return {
        ...baseProps,
        selectedWorkouts: selectedValues,
        onNext: workouts =>
          handleStepComplete(currentStep, { [hubspotProperty]: workouts }),
        canSkip,
      };
    }

    if (StepComponent === MotivationStep) {
      const hubspotProperty = stepData.hubspotProperty;
      const selectedValues = onboardingData[hubspotProperty] || [];

      return {
        ...baseProps,
        selectedMotivations: selectedValues,
        onNext: motivations =>
          handleStepComplete(currentStep, { [hubspotProperty]: motivations }),
        canSkip,
      };
    }

    if (StepComponent === FitnessLevelStep) {
      // Handle slider type with multiple hubspot properties
      const sliderProps = {};

      if (stepData.sliders) {
        stepData.sliders.forEach(slider => {
          const value = onboardingData[slider.hubspotProperty] || '';
          sliderProps[slider.hubspotProperty] = value;
        });
      }

      return {
        ...baseProps,
        ...sliderProps, // Spread all slider values
        onNext: sliderData => handleStepComplete(currentStep, sliderData),
        canSkip,
        isLastStep,
      };
    }

    return baseProps;
  };

  // Render step content dynamically based on API data
  const renderStepContent = () => {
    const stepData = getCurrentStepData();

    if (!stepData) {
      return <div>Loading step data...</div>;
    }

    const StepComponent = getStepComponent(stepData);

    if (!StepComponent) {
      return <div>Unknown step type: {stepData.type}</div>;
    }

    const componentProps = getComponentProps(stepData, StepComponent);

    return <StepComponent {...componentProps} />;
  };

  return (
    <HeaderFooterLayoutOnboarding
      currentStep={currentStep}
      totalSteps={totalSteps}
      onBackClick={canGoBack ? () => handleStepBack(currentStep) : undefined}
    >
      {renderStepContent()}
    </HeaderFooterLayoutOnboarding>
  );
};

OnboardingProcess.propTypes = {
  loadOnboardingSteps: PropTypes.func,
  stepsData: PropTypes.object,
  isLoadingSteps: PropTypes.bool,
  submitOnboardingSteps: PropTypes.func,
  lastSubmittedResponse: PropTypes.object,
};

OnboardingProcess.defaultProps = {
  stepsData: {},
  isLoadingSteps: false,
  loadOnboardingSteps: () => {},
  submitOnboardingSteps: () => {},
  submissionSuccess: false,
  error: null,
};

export default OnboardingProcess;
